/**
 * The photographer's five-circle workflow row: Issue / No Keys / In Progress /
 * Cancelled / Done — six on the Reshoots sheet, where Verified follows Done
 * (Ian, 2026-11-04: Done means shot, Verified means the new photos were seen
 * live on the website). Tap to set, tap the active circle to clear. Purely
 * presentational — the optimistic state, per-VIN save chains, and error
 * handling live in PhotoListTable, which passes the active status and tap
 * handler down. Shared by the mobile vehicle cards and the notes modal (the
 * desktop table has no circle row of its own — the modal is its surface).
 */
import React from "react"

import type { ProgressStatus } from "../../api/photoList"
import { THEME } from "../../config/theme"

// The photographer's decision order, left to right (Ian, 2026-09-01):
// blockers first — something wrong with the car (danger red, leftmost so it
// pops when scanning the list), keys missing (warning) — then the working
// state (info), then the terminal states: shoot stopped (neutral gray; red
// belongs to Issue) and finished (success, anchored in the prime end spot).
const PROGRESS_STEPS: Array<{
  status: ProgressStatus
  label: string
  color: string
}> = [
  { status: "issue", label: "Issue", color: THEME.colors.semantic.danger },
  { status: "no_keys", label: "No Keys", color: THEME.colors.semantic.warning },
  { status: "in_progress", label: "In Progress", color: THEME.colors.semantic.info },
  { status: "cancelled", label: "Cancelled", color: THEME.colors.neutral.light.textSecondary },
  { status: "done", label: "Done", color: THEME.colors.semantic.success },
]

// The Reshoots sheet's sixth step, in the brand navy so it reads apart from
// Done's green.
const VERIFIED_STEP: (typeof PROGRESS_STEPS)[number] = {
  status: "verified",
  label: "Verified",
  color: THEME.colors.navy,
}

export default function ProgressCircles({
  active,
  onTap,
  includeVerified = false,
}: {
  active: ProgressStatus | null
  onTap: (status: ProgressStatus) => void
  /** True on the Reshoots sheet's rows only. */
  includeVerified?: boolean
}) {
  const steps = includeVerified ? [...PROGRESS_STEPS, VERIFIED_STEP] : PROGRESS_STEPS
  // Six labels at phone width need a little less room each than five.
  const compact = includeVerified
  return (
    <div style={progressRowStyle}>
      {steps.map((step) => {
        const isActive = active === step.status
        return (
          <button
            key={step.status}
            type="button"
            onClick={() => onTap(step.status)}
            aria-pressed={isActive}
            style={progressStepStyle(compact)}
          >
            <span style={progressCircleStyle(isActive, step.color)}>
              {isActive ? "✓" : ""}
            </span>
            <span style={progressLabelStyle(isActive, compact)}>{step.label}</span>
          </button>
        )
      })}
    </div>
  )
}

const progressRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  borderTop: `1px solid ${THEME.colors.silver}`,
  marginTop: THEME.spacing.xs,
  paddingTop: THEME.spacing.xs,
}

const progressStepStyle = (compact: boolean): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  background: "none",
  border: "none",
  padding: compact ? "2px 1px" : "2px 4px",
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
})

const progressCircleStyle = (
  isActive: boolean,
  color: string,
): React.CSSProperties => ({
  // Visually small; the tap target stays the whole labeled button around it.
  width: 16,
  height: 16,
  borderRadius: "50%",
  border: `1.5px solid ${isActive ? color : THEME.colors.silver}`,
  background: isActive ? color : THEME.colors.neutral.light.surface,
  color: THEME.colors.white,
  fontSize: 9,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
})

const progressLabelStyle = (isActive: boolean, compact: boolean): React.CSSProperties => ({
  fontSize: compact ? 8 : 9,
  fontWeight: isActive ? 600 : 500,
  letterSpacing: compact ? "0.1px" : "0.3px",
  textTransform: "uppercase",
  color: isActive
    ? THEME.colors.neutral.light.textPrimary
    : THEME.colors.neutral.light.textSecondary,
  whiteSpace: "nowrap",
})
